import React, { useState, useEffect } from "react";
import API from "../api";
import { Card, Input, Button, Toast, SectionHeader } from "../components/UI";
import { useAuth } from "../context/AuthContext";

const PLANS = [
  {
    key: "basic",
    name: "Basic",
    price: "₹1,999/month",
    setup: "₹3,999 setup",
    color: "#4d9fff",
    features: ["1 WhatsApp Bot", "Lead Capture", "Google Sheet Sync", "Email Notifications"],
  },
  {
    key: "standard",
    name: "Standard",
    price: "₹2,499/month",
    setup: "₹7,999 setup",
    color: "#25d366",
    popular: true,
    features: ["Everything in Basic", "Custom Bot Flow", "All Notification Channels", "3 Month Support"],
  },
  {
    key: "premium",
    name: "Premium",
    price: "₹4,999/month",
    setup: "Custom",
    color: "#ff9500",
    features: ["Everything in Standard", "Multiple Bots", "Priority Support", "Custom Integration"],
  },
];

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

export default function ClientSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [payingPlan, setPayingPlan] = useState(null);
  const [toast, setToast] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [planEndDate, setPlanEndDate] = useState(null);
  const [form, setForm] = useState({
    botProfile_address: "",
    botProfile_phone: "",
    botProfile_hours: "",
    botProfile_offers: "",
    botProfile_extraInfo: "",
    notify_email_enabled: false,
    notify_email: "",
    notify_whatsapp_enabled: false,
    notify_whatsapp: "",
    notify_telegram_enabled: false,
    notify_telegram_chatId: "",
    googleSheetUrl: "",
  });

  useEffect(() => {
    const id = user?.clientId;
    if (!id) return;
    setClientId(id);
    API.get(`/client/${id}`)
      .then(r => {
        const c = r.data;
        setIsConnected(!!(c.whatsapp?.wabaId && c.whatsapp?.phoneId && c.whatsapp?.phoneId !== "MOCK_PHONE_ID"));
        setPlanEndDate(c.plan_end_date || null);
        setForm({
          botProfile_address: c.botProfile?.address || "",
          botProfile_phone: c.botProfile?.phone || "",
          botProfile_hours: c.botProfile?.hours || "",
          botProfile_offers: c.botProfile?.offers || "",
          botProfile_extraInfo: c.botProfile?.extraInfo || "",
          notify_email_enabled: !!c.notifications?.email,
          notify_email: c.notifications?.email || "",
          notify_whatsapp_enabled: !!c.notifications?.whatsapp,
          notify_whatsapp: c.notifications?.whatsapp || "",
          notify_telegram_enabled: !!(c.notifications?.telegram?.chatId),
          notify_telegram_chatId: c.notifications?.telegram?.chatId || "",
          googleSheetUrl: c.googleSheetUrl || "",
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleCheck = e => setForm(p => ({ ...p, [e.target.name]: e.target.checked }));

  const handleWhatsAppConnect = async () => {
    if (!clientId) return;
    setConnectLoading(true);
    try {
      const res = await API.get(`/auth/onboard-url/${clientId}`);
      window.location.href = res.data.onboardUrl;
    } catch (err) {
      setToast({ message: "WhatsApp connect failed. Try again.", type: "error" });
      setConnectLoading(false);
    }
  };

  const handlePayment = async (plan) => {
    setPayingPlan(plan.key);
    try {
      const res = await API.post("/payment/create-order", { plan: plan.key });
      const { orderId, amount, currency, name } = res.data;

      const options = {
        key: RAZORPAY_KEY,
        amount,
        currency,
        name: "BotSaathi",
        description: name,
        order_id: orderId,
        handler: async (response) => {
          try {
            await API.post("/payment/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: plan.key,
            });
            setToast({ message: `${plan.name} plan activated! 🎉`, type: "success" });
            setPlanEndDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString());
          } catch {
            setToast({ message: "Payment verification failed. Contact support.", type: "error" });
          }
        },
        prefill: { email: user?.email },
        theme: { color: "#25d366" },
        modal: { ondismiss: () => setPayingPlan(null) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setToast({ message: "Payment initiation failed.", type: "error" });
    } finally {
      setPayingPlan(null);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!clientId) return;
    setSaving(true);
    try {
      await API.put(`/client/${clientId}`, {
        googleSheetUrl: form.googleSheetUrl,
        botProfile: {
          address: form.botProfile_address,
          phone: form.botProfile_phone,
          hours: form.botProfile_hours,
          offers: form.botProfile_offers,
          extraInfo: form.botProfile_extraInfo,
        },
        notifications: {
          email: form.notify_email_enabled ? form.notify_email : "",
          whatsapp: form.notify_whatsapp_enabled ? form.notify_whatsapp : "",
          telegram: {
            chatId: form.notify_telegram_enabled ? form.notify_telegram_chatId : "",
          }
        }
      });
      setToast({ message: "Settings saved!", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.error || "Failed", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, color: "var(--text-muted)" }}>Loading...</div>;

  const isPlanActive = planEndDate && new Date(planEndDate) > new Date();

  return (
    <div className="fade-in" style={{ maxWidth: 600 }}>
      <SectionHeader title="My Settings" subtitle="Apni bot profile aur plan manage karo" />

      {/* ── Subscription Plans ── */}
      <Card style={{ marginBottom: 16 }}>
        <div style={s.sectionLabel}>Subscription Plan</div>

        {isPlanActive && (
          <div style={s.activePlanBox}>
            <span style={{ fontSize: 16 }}>✅</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Plan Active</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                Valid till: {new Date(planEndDate).toLocaleDateString("en-IN")}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 16 }}>
          {PLANS.map(plan => (
            <div key={plan.key} style={{ ...s.planCard, border: `1px solid ${plan.popular ? plan.color : "var(--border)"}`, position: "relative" }}>
              {plan.popular && (
                <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: plan.color, padding: "2px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: "#000", whiteSpace: "nowrap" }}>
                  ⭐ Popular
                </div>
              )}
              <div style={{ fontSize: 14, fontWeight: 700, color: plan.color, marginBottom: 4 }}>{plan.name}</div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{plan.price}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>{plan.setup}</div>
              {plan.features.map((f, i) => (
                <div key={i} style={{ fontSize: 11, color: "var(--text-secondary)", padding: "2px 0" }}>✓ {f}</div>
              ))}
              <button
                onClick={() => handlePayment(plan)}
                disabled={payingPlan === plan.key}
                style={{ ...s.payBtn, background: plan.color, marginTop: 12 }}
              >
                {payingPlan === plan.key ? "Loading..." : "Subscribe"}
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* ── Settings Form ── */}
      <Card>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          <div style={s.sectionLabel}>WhatsApp Connection</div>
          {isConnected ? (
            <div style={s.connectedBox}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={s.connectedDot} />
                <div style={{ fontSize: 13, fontWeight: 600, color: "#34c759" }}>WhatsApp Connected</div>
              </div>
              <button type="button" onClick={handleWhatsAppConnect} style={s.reconnectBtn}>Reconnect</button>
            </div>
          ) : (
            <div style={s.connectBox}>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 12 }}>
                📱 <strong>Connect karo</strong> — apna WhatsApp Business account link karo.
              </div>
              <button type="button" onClick={handleWhatsAppConnect} disabled={connectLoading} style={s.connectBtn}>
                {connectLoading ? "Redirecting..." : "🔗 Connect WhatsApp Business"}
              </button>
            </div>
          )}

          <div style={{ height: 1, background: "var(--border)" }} />
          <div style={s.sectionLabel}>Bot Profile</div>
          <Input label="Address" name="botProfile_address" value={form.botProfile_address} onChange={handleChange} placeholder="Showroom / Office address" />
          <Input label="Contact Number" name="botProfile_phone" value={form.botProfile_phone} onChange={handleChange} placeholder="9876543210" />
          <Input label="Business Hours" name="botProfile_hours" value={form.botProfile_hours} onChange={handleChange} placeholder="Mon-Sat 9AM-6PM" />
          <Input label="Current Offers" name="botProfile_offers" value={form.botProfile_offers} onChange={handleChange} placeholder="Free first service, EMI options etc." />
          <Input label="Extra Info" name="botProfile_extraInfo" value={form.botProfile_extraInfo} onChange={handleChange} placeholder="Jo bhi bot ko pata hona chahiye" />

          <div style={{ height: 1, background: "var(--border)" }} />
          <div style={s.sectionLabel}>Google Sheet (Optional)</div>
          <Input label="Webhook URL" name="googleSheetUrl" value={form.googleSheetUrl} onChange={handleChange} placeholder="Apps Script Web App URL" />

          <div style={{ height: 1, background: "var(--border)" }} />
          <div style={s.sectionLabel}>Notifications</div>
          <div style={s.infoBox}>📢 Hot lead aane pe alert milega.</div>

          <div style={s.notifRow}>
            <label style={s.checkLabel}>
              <input type="checkbox" name="notify_email_enabled" checked={form.notify_email_enabled} onChange={handleCheck} style={s.checkbox} />
              <span>📧 Email Alert</span>
            </label>
            {form.notify_email_enabled && (
              <Input name="notify_email" value={form.notify_email} onChange={handleChange} placeholder="aapka@email.com" />
            )}
          </div>

          <div style={s.notifRow}>
            <label style={s.checkLabel}>
              <input type="checkbox" name="notify_whatsapp_enabled" checked={form.notify_whatsapp_enabled} onChange={handleCheck} style={s.checkbox} />
              <span>📱 WhatsApp Alert</span>
            </label>
            {form.notify_whatsapp_enabled && (
              <Input name="notify_whatsapp" value={form.notify_whatsapp} onChange={handleChange} placeholder="919876543210" />
            )}
          </div>

          <div style={s.notifRow}>
            <label style={s.checkLabel}>
              <input type="checkbox" name="notify_telegram_enabled" checked={form.notify_telegram_enabled} onChange={handleCheck} style={s.checkbox} />
              <span>✈️ Telegram Alert</span>
            </label>
            {form.notify_telegram_enabled && (
              <>
                <div style={s.telegramInfo}>
                  📱 <b>Steps:</b><br />
                  1. Telegram pe <code style={{ background: "rgba(255,255,255,0.08)", padding: "1px 6px", borderRadius: 4 }}>@Wappalert_bot</code> open karo<br />
                  2. <code style={{ background: "rgba(255,255,255,0.08)", padding: "1px 6px", borderRadius: 4 }}>/start</code> bhejo<br />
                  3. Chat ID copy karo aur neeche paste karo
                </div>
                <Input name="notify_telegram_chatId" value={form.notify_telegram_chatId} onChange={handleChange} placeholder="Chat ID — @Wappalert_bot se lo" />
              </>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "✓ Save Settings"}</Button>
          </div>

        </form>
      </Card>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const s = {
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: "var(--text-muted)",
    textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "var(--font-mono)",
    marginBottom: 4,
  },
  activePlanBox: {
    display: "flex", alignItems: "center", gap: 12,
    background: "rgba(37,211,102,0.07)", border: "1px solid rgba(37,211,102,0.2)",
    borderRadius: "var(--radius)", padding: "10px 14px",
  },
  planCard: {
    background: "var(--bg-elevated)", borderRadius: "var(--radius)",
    padding: 16, display: "flex", flexDirection: "column",
  },
  payBtn: {
    width: "100%", padding: "9px", borderRadius: "var(--radius)",
    color: "#000", border: "none", cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "var(--font-body)",
  },
  infoBox: {
    fontSize: 12, color: "var(--accent-blue)", background: "rgba(77,159,255,0.08)",
    border: "1px solid rgba(77,159,255,0.2)", borderRadius: "var(--radius)",
    padding: "8px 12px", lineHeight: 1.6
  },
  telegramInfo: {
    fontSize: 12, color: "var(--text-secondary)", background: "rgba(37,211,102,0.06)",
    border: "1px solid rgba(37,211,102,0.2)", borderRadius: "var(--radius)",
    padding: "10px 12px", lineHeight: 1.9
  },
  notifRow: {
    display: "flex", flexDirection: "column", gap: 8,
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    borderRadius: "var(--radius)", padding: "12px"
  },
  checkLabel: {
    display: "flex", alignItems: "center", gap: 10,
    cursor: "pointer", fontSize: 13, color: "var(--text-primary)", fontWeight: 500
  },
  checkbox: { width: 16, height: 16, cursor: "pointer", accentColor: "var(--accent)" },
  connectBox: {
    padding: "16px", borderRadius: "var(--radius)",
    background: "rgba(77,159,255,0.06)", border: "1px solid rgba(77,159,255,0.2)",
  },
  connectBtn: {
    width: "100%", padding: "11px 20px", borderRadius: "var(--radius)",
    background: "#25d366", color: "#000", border: "none",
    cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "var(--font-body)",
  },
  connectedBox: {
    padding: "12px 16px", borderRadius: "var(--radius)",
    background: "rgba(52,199,89,0.07)", border: "1px solid rgba(52,199,89,0.25)",
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  },
  connectedDot: {
    width: 8, height: 8, borderRadius: "50%",
    background: "#34c759", boxShadow: "0 0 6px rgba(52,199,89,0.6)", flexShrink: 0,
  },
  reconnectBtn: {
    padding: "6px 14px", borderRadius: "var(--radius)",
    background: "rgba(52,199,89,0.12)", color: "#34c759",
    border: "1px solid rgba(52,199,89,0.25)",
    cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "var(--font-body)",
  },
};